import React, { useState, useEffect, useCallback } from 'react'
import { Form, Button } from 'react-bootstrap'
import styled from 'styled-components'
import { LayerInfo } from './types'
import { PredictionMetadata } from '../../../../types/models/prediction'

// Styled components
const PredictionsPanelContainer = styled.div<{ hidden?: boolean }>`
  position: absolute;
  top: 80px;
  right: 10px;
  bottom: 50px;
  z-index: 1000;
  background-color: ${(props) => props.theme.colors.background.dark || '#1a1a1a'};
  background-color: rgba(26, 26, 26, 0.9);
  border-radius: 8px;
  padding: 12px;
  width: 300px;
  color: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  overflow-y: auto;
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  transform: ${(props) => (props.hidden ? 'translateX(110%)' : 'translateX(0)')};

  &:hover {
    background-color: rgba(26, 26, 26, 0.95);
  }

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 4px;

    &:hover {
      background-color: rgba(255, 255, 255, 0.3);
    }
  }
`

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`

const PredictionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const PredictionCard = styled.div<{ isActive?: boolean }>`
  background-color: ${(props) =>
    props.isActive ? 'rgba(66, 153, 225, 0.3)' : 'rgba(40, 40, 40, 0.9)'};
  border-radius: 8px;
  padding: 10px;
  border: 1px solid
    ${(props) => (props.isActive ? 'rgba(66, 153, 225, 0.6)' : 'rgba(255, 255, 255, 0.1)')};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateX(-5px);
    border-color: rgba(255, 255, 255, 0.3);
    background-color: ${(props) =>
      props.isActive ? 'rgba(66, 153, 225, 0.4)' : 'rgba(50, 50, 50, 0.9)'};
  }

  .prediction-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .prediction-class {
    font-weight: bold;
    color: white;
    font-size: 0.9rem;
  }

  .prediction-confidence {
    background-color: rgba(0, 0, 0, 0.3);
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8rem;
  }

  .prediction-coords {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.7);
    margin-top: 5px;
  }
`

const ColorIndicator = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  margin-right: 8px;
  display: inline-block;
`

const EmptyPredictionsMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: rgba(255, 255, 255, 0.6);

  i {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    display: block;
    opacity: 0.7;
  }

  p {
    margin-bottom: 0.5rem;
  }
`

const PredictionFilters = styled.div`
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`

const NavigationBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
`

const NavButton = styled.button`
  background-color: rgba(66, 153, 225, 0.2);
  border: 1px solid rgba(66, 153, 225, 0.4);
  color: white;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: rgba(66, 153, 225, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: rgba(66, 153, 225, 0.1);
  }
`

// Get color for a class name
const getClassColor = (className: string): string => {
  const colorMap: Record<string, string> = {
    combatant: '#FF5733',
    cargo: '#33FF57',
    tanker: '#3357FF',
    fishing: '#FF33A8',
    passenger: '#33FFF5',
    tug: '#F533FF',
    default: '#FFFF33',
  }
  return colorMap[className] || colorMap['default']
}

interface PredictionsPanelProps {
  layers: LayerInfo[]
  hidden: boolean
  zoomToPrediction: (layerId: string, prediction: PredictionMetadata) => void
  onTogglePanel?: () => void
}

const PredictionsPanel: React.FC<PredictionsPanelProps> = ({
  layers,
  hidden,
  zoomToPrediction,
  onTogglePanel,
}) => {
  const [activePrediction, setActivePrediction] = useState<string | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const [confidenceThreshold, setConfidenceThreshold] = useState<number>(0.1)
  const [filteredClassName, setFilteredClassName] = useState<string>('')

  // Get all visible layers with predictions
  const visibleLayersWithPredictions = layers.filter(
    (layer) => layer.isVisible && layer.predictions && layer.predictions.length > 0
  )

  // Get unique class names from all predictions
  const uniqueClassNames = Array.from(
    new Set(
      visibleLayersWithPredictions
        .flatMap((layer) => layer.predictions || [])
        .map((pred) => pred.class_name)
    )
  ).sort()

  // Get filtered predictions that meet the confidence threshold
  const filteredPredictions = visibleLayersWithPredictions
    .flatMap((layer) =>
      (layer.predictions || [])
        .filter(
          (pred) =>
            pred.confidence >= confidenceThreshold &&
            (filteredClassName === '' || pred.class_name === filteredClassName)
        )
        .map((pred) => ({
          layerId: layer.id,
          prediction: pred,
        }))
    )
    .sort((a, b) => b.prediction.confidence - a.prediction.confidence)

  // Handle prediction click
  const handlePredictionClick = useCallback(
    (layerId: string, prediction: PredictionMetadata, index: number) => {
      const predictionId = `${layerId}-${prediction.class_index}`
      setActivePrediction(predictionId)
      setActiveIndex(index)
      zoomToPrediction(layerId, prediction)
    },
    [zoomToPrediction]
  )

  // Navigate to next/previous prediction
  const goToNextPrediction = useCallback(() => {
    if (filteredPredictions.length === 0) return

    const nextIndex = activeIndex < filteredPredictions.length - 1 ? activeIndex + 1 : 0
    const { layerId, prediction } = filteredPredictions[nextIndex]
    handlePredictionClick(layerId, prediction, nextIndex)
  }, [activeIndex, filteredPredictions, handlePredictionClick])

  const goToPrevPrediction = useCallback(() => {
    if (filteredPredictions.length === 0) return

    const prevIndex = activeIndex > 0 ? activeIndex - 1 : filteredPredictions.length - 1
    const { layerId, prediction } = filteredPredictions[prevIndex]
    handlePredictionClick(layerId, prediction, prevIndex)
  }, [activeIndex, filteredPredictions, handlePredictionClick])

  const handleTogglePanel = () => {
    if (onTogglePanel) {
      onTogglePanel()
    }
  }

  // Navigate with keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (hidden) return

      if (e.key === 'ArrowDown' || e.key === 'j') {
        goToNextPrediction()
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        goToPrevPrediction()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [hidden, activeIndex, filteredPredictions, goToNextPrediction, goToPrevPrediction])

  return (
    <PredictionsPanelContainer hidden={hidden}>
      <PanelHeader>
        <h5 style={{ margin: 0, fontSize: '1rem' }}>Predictions</h5>
        {onTogglePanel && (
          <Button
            variant="outline-light"
            size="sm"
            onClick={handleTogglePanel}
            style={{ padding: '2px 6px' }}
          >
            <i className="bi bi-x-lg" style={{ fontSize: '12px' }}></i>
          </Button>
        )}
      </PanelHeader>

      <PredictionFilters>
        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>
            Confidence Threshold: {confidenceThreshold.toFixed(2)}
          </Form.Label>
          <Form.Range
            min={0}
            max={1}
            step={0.05}
            value={confidenceThreshold}
            onChange={(e) => setConfidenceThreshold(parseFloat(e.target.value))}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>
            Filter by Class
          </Form.Label>
          <Form.Select
            size="sm"
            value={filteredClassName}
            onChange={(e) => setFilteredClassName(e.target.value)}
            style={{
              backgroundColor: 'rgba(60, 60, 60, 0.9)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <option value="">All Classes</option>
            {uniqueClassNames.map((className) => (
              <option key={className} value={className}>
                {className}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </PredictionFilters>

      {filteredPredictions.length > 0 && (
        <NavigationBar>
          <NavButton
            onClick={goToPrevPrediction}
            disabled={filteredPredictions.length <= 1}
            title="Previous prediction (Up arrow / k)"
          >
            <i className="bi bi-arrow-up"></i>
          </NavButton>

          <div style={{ fontSize: '0.8rem' }}>
            {activeIndex + 1} of {filteredPredictions.length}
          </div>

          <NavButton
            onClick={goToNextPrediction}
            disabled={filteredPredictions.length <= 1}
            title="Next prediction (Down arrow / j)"
          >
            <i className="bi bi-arrow-down"></i>
          </NavButton>
        </NavigationBar>
      )}

      {filteredPredictions.length > 0 ? (
        <PredictionsList>
          {filteredPredictions.map(({ layerId, prediction }, index) => {
            const predictionId = `${layerId}-${prediction.class_index}`
            const { xmin, ymin, xmax, ymax } = prediction.bounding_boxes || {
              xmin: 0,
              ymin: 0,
              xmax: 0,
              ymax: 0,
            }
            const layerName = layers.find((l) => l.id === layerId)?.originalFile?.name || 'Layer'
            return (
              <PredictionCard
                key={predictionId}
                isActive={activePrediction === predictionId}
                onClick={() => handlePredictionClick(layerId, prediction, index)}
              >
                <div className="prediction-header">
                  <div className="prediction-class">
                    <ColorIndicator color={getClassColor(prediction.class_name)} />
                    <span style={{ position: 'relative', top: '-1px' }}>
                      {prediction.class_name}
                      <span
                        style={{
                          fontSize: '10px',
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          borderRadius: '3px',
                          padding: '1px 4px',
                          marginLeft: '5px',
                        }}
                      >
                        #{index + 1}
                      </span>
                    </span>
                  </div>
                  <div className="prediction-confidence">
                    {(prediction.confidence * 100).toFixed(1)}%
                  </div>
                </div>
                <div className="prediction-coords">
                  <small>
                    X: {xmin ? Math.round(xmin) : 0}-{xmax ? Math.round(xmax) : 0}, Y:{' '}
                    {ymin ? Math.round(ymin) : 0}-{ymax ? Math.round(ymax) : 0}
                  </small>
                </div>
                <div
                  style={{
                    fontSize: '0.7rem',
                    color: 'rgba(255,255,255,0.5)',
                    marginTop: '3px',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <i
                    className="bi bi-layers"
                    style={{ marginRight: '3px', fontSize: '0.6rem' }}
                  ></i>
                  {layerName}
                </div>
              </PredictionCard>
            )
          })}
        </PredictionsList>
      ) : (
        <EmptyPredictionsMessage>
          <i className="bi bi-binoculars"></i>
          <p>No predictions found</p>
          <small>Run inference on a layer or adjust the filters</small>
        </EmptyPredictionsMessage>
      )}
    </PredictionsPanelContainer>
  )
}

export default PredictionsPanel
